<section style="margin: 16px 0; max-width: 560px" aria-label="Monthly top-up schedule">
  <h3>Monthly allowance funding</h3>
  @if (fundingStatus) {
    <p role="status"><strong>{{ fundingStatusLabel }}</strong> · {{ configuredMonthlyAmount | currency }} per member per month, subject to member overrides.</p>
  }
  @if (fundingStatus === 'recovery_required') {
    <p>Allowance is configured, but no recurring payment is scheduled.</p>
    @if (fundingActionsEnabled) {
      <p>Review funding recovery to preview the members, monthly total, and next payment date before restoring future funding.</p>
    }
    <button mat-stroked-button (click)="reviewRecovery()" [disabled]="!fundingActionsEnabled || disabled || loading || pending || hasUnsavedDay">Review funding recovery</button>
  }
  @if (fundingStatus === 'out_of_sync') {
    <p>The scheduled funding does not match the current team settings.</p>
    @if (fundingActionsEnabled) {
      <p>Retry funding update to apply the saved settings to future monthly payments.</p>
    }
    <button mat-stroked-button (click)="synchronize()" [disabled]="!fundingActionsEnabled || disabled || loading || pending">Retry funding update</button>
  }
  @if (!loading && !fundingActionsEnabled && (fundingStatus === 'recovery_required' || fundingStatus === 'out_of_sync')) {
    <p role="status">Funding recovery and synchronization are not enabled for this team. You can continue editing team settings using the existing controls. Saving settings alone does not confirm that recurring funding is scheduled or up to date.</p>
  }
  @if (recoveryPreview) {
    <div role="region" aria-label="Review allowance funding recovery">
      <p>Restore future monthly funding for {{ recoveryPreview.memberCount }} active members.</p>
      <p>Allowance total: {{ recoveryPreview.allowanceTotalCents / 100 | currency }}. Fees: {{ recoveryPreview.feeCents / 100 | currency }}.</p>
      <p><strong>First payment: {{ recoveryPreview.nextFundingDate | date:'mediumDate':'UTC' }} (UTC).</strong> No immediate funding or missed-month catch-up is included.</p>
      <button mat-raised-button color="primary" (click)="confirmRecovery()" [disabled]="!fundingActionsEnabled || disabled || loading">Restore future monthly funding</button>
      <button mat-button (click)="recoveryPreview = null" [disabled]="loading">Cancel</button>
    </div>
  }
  @if (fundingStatus === 'recovery_pending') {
    <p>A recovery is being processed. Reload to check its outcome before taking further action.</p>
    <button mat-button (click)="load()" [disabled]="loading">Reload funding status</button>
  }
  @if (fundingStatus === 'attention_required') {
    <p>{{ fundingAttentionMessage }}</p>
  }
  @if (fundingStatus === 'attention_required' || fundingStatus === 'setup_required' || fundingStatus === 'out_of_sync' || fundingStatus === 'recovery_required') {
    <button mat-button (click)="load()" [disabled]="loading">Reload funding status</button>
  }
  <h4>Monthly top-up day</h4>
  @if (nextFundingDate) {
    <p>Next scheduled payment: {{ nextFundingDate | date:'mediumDate':'UTC' }} (UTC).</p>
  }
  @if (!hasSubscription && fundingStatus === 'setup_required') {
    <p>Check the team’s default funding method and complete any required verification. Choose the monthly top-up day, then use the member funding flow to review and confirm initial allowances.</p>
  }
  <p>Payments start on this day. Member balances become available after payment processing, which can take several business days.</p>
  <div style="display: flex; align-items: flex-start; gap: 12px">
    <mat-form-field appearance="outline" subscriptSizing="dynamic" style="flex: 1; min-width: 0; max-width: 320px">
      <mat-label>Top-up day</mat-label>
      <mat-select [(ngModel)]="day" (ngModelChange)="preview = null; recoveryPreview = null; message = ''" [disabled]="disabled || loading || pending">
        @for (value of days; track value) {
          <mat-option [value]="value">
            {{ value }}{{ value === 1 || value === 21 ? 'st' : value === 2 || value === 22 ? 'nd' : value === 3 || value === 23 ? 'rd' : 'th' }} day of month
          </mat-option>
        }
      </mat-select>
    </mat-form-field>
    <button mat-stroked-button type="button" style="flex-shrink: 0; margin-top: 10px" (click)="review()" [disabled]="disabled || loading || pending || !day || day === currentDay">
      Set New Top-up Day
    </button>
  </div>
  @if (hasUnsavedDay && !pending) {
    <p role="status">This day has not been saved. Click Set New Top-up Day and confirm the change before funding member allowances.</p>
  }
  @if (pending) {
    <p>A date change is pending. Review it to retry the same change.</p>
  }
  @if (pending) {
    <button mat-stroked-button type="button" (click)="review()" [disabled]="disabled || loading || !day">Review pending change</button>
  }
  @if (loading) {
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
  }
  @if (error) {
    <p role="alert">{{ error }}</p>
  }
  @if (error) {
    <button mat-button type="button" (click)="load()" [disabled]="loading">Reload schedule</button>
  }
  @if (message) {
    <p role="status">{{ message }}</p>
  }
  @if (preview) {
    <div style="padding: 16px; margin-top: 12px; border: 1px solid #ccc; border-radius: 8px">
      @if (preview.currentFundingDate) {
        <p>Already-funded period ends {{ preview.currentFundingDate | date:'mediumDate':'UTC' }}.</p>
      }
      @if (preview.subscriptionId) {
        <p>
          Prorated allowance funding for the intervening days: {{ preview.transitionAllowanceCents / 100 | currency }}.<br>
          Service fee: {{ preview.transitionFeeCents / 100 | currency }}.<br>
          <strong>One-time funding total: {{ (preview.transitionAllowanceCents + preview.transitionFeeCents) / 100 | currency }}.</strong>
        </p>
      }
      @if (preview.subscriptionId) {
        <p>This starts the one-time payment now. Any available billing credit is applied before charging the remaining amount.</p>
      }
      @if (preview.subscriptionId) {
        <p>
          Next full payment: {{ preview.recurringChargeTotalCents / 100 | currency }} on {{ preview.nextFundingDate | date:'mediumDate':'UTC' }}, then every month on day {{ preview.monthlyTopupDay }}.
        </p>
      }
      @if (!preview.subscriptionId) {
        <p>Save day {{ preview.monthlyTopupDay }} for monthly payments. Initial funding will be quoted separately when you fund member allowances.</p>
      }
      <button mat-raised-button color="primary" type="button" (click)="confirm()" [disabled]="loading || disabled">Confirm top-up date</button>
      <button mat-button type="button" (click)="preview = null" [disabled]="loading">Cancel</button>
    </div>
  }
</section>
